import { DateTime } from 'luxon';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { OrderAndSalesCountResponse } from '../types/api/order.types';
import {
  Order,
  OrderStatus,
  SimpleOrderEntity,
  VDriveEntity,
} from '../types/order.types';
import { VDriveProvider } from '../types/shared.types';

type ModalType = string | number;
type VDrive = VDriveEntity;

export type DataHistory = {
  orders?: SimpleOrderEntity[];
} & OrderAndSalesCountResponse;

interface OrdersState {
  dataOrders: SimpleOrderEntity[];
  dataHistory: DataHistory[];
  detailsModal: ModalType | null;
  deleteManualOrderModal: Order | null;
  printOrderModal: Order | null;
  loading: boolean;
  orderToEdit: Order | null;
  hasNewOrder: boolean;

  updateOrder: (updatedDelivery: SimpleOrderEntity) => void;
  updateActiveOrderAndSort: (updatedDelivery: SimpleOrderEntity) => void;

  setHasNewOrder: (value: boolean) => void;
  setOrderToEdit: (value: Order | null) => void;
  setDataOrders: (value: SimpleOrderEntity[]) => void;
  setDataHistory: (value: OrderAndSalesCountResponse[]) => void;
  setLoading: (value: boolean) => void;
  clearDataOrders: () => void;
  setDeleteManualOrderModal: (value: Order | null) => void;
  setPrintOrderModal: (value: Order | null) => void;

  removeHistoryOrderById: (order: Order) => void;
  removeOrderById: (id: number) => void;

  sortActiveDataOrders: (id: string) => void;

  assignVDriveProviderOnList: (id: number, provider: VDriveProvider) => void;
  changeVDriveOnList: (id: number | string, vDrive: VDrive | null) => void;
}

export const useOrders = create<OrdersState>()(
  devtools(
    (set) => ({
    dataOrders: [],
    dataHistory: [],
    detailsModal: null,
    deleteManualOrderModal: null,
    printOrderModal: null,
    loading: false,
    orderToEdit: null,
    hasNewOrder: false,

    updateOrder: (updatedDelivery) =>
      set((state) => {
        const index = state.dataOrders.findIndex(
          (order) => order.id === updatedDelivery.id
        );
        if (index !== -1) {
          const updatedList = [...state.dataOrders];
          updatedList[index] = updatedDelivery;
          return { dataOrders: updatedList };
        }
        return {};
      }),

    updateActiveOrderAndSort: (updatedDelivery) =>
      set((state) => {
        const index = state.dataOrders.findIndex(
          (order) => order.id === updatedDelivery.id
        );
        if (index !== -1) {
          const updatedList = [...state.dataOrders];
          updatedList[index] = updatedDelivery;

          const newOrders = updatedList.filter(
            (order) => order.status === OrderStatus.new
          );
          const acceptedOrders = updatedList.filter(
            (order) => order.status === OrderStatus.accepted
          );

          const sortedOrders = [
            ...newOrders,
            ...acceptedOrders.filter(
              (order) => order.id === updatedDelivery.id
            ),
            ...acceptedOrders.filter(
              (order) => order.id !== updatedDelivery.id
            ),
          ];

          return { dataOrders: sortedOrders };
        }
        return {};
      }),

    setHasNewOrder: (value) => set(() => ({ hasNewOrder: value })),
    setOrderToEdit: (value) => set(() => ({ orderToEdit: value })),
    setDataOrders: (value) => set(() => ({ dataOrders: value })),
    setDataHistory: (value) => set(() => ({ dataHistory: value })),
    setLoading: (value) => set(() => ({ loading: value })),
    clearDataOrders: () => set(() => ({ dataOrders: [] })),
    setDeleteManualOrderModal: (value) =>
      set(() => ({ deleteManualOrderModal: value })),
    setPrintOrderModal: (value) => set(() => ({ printOrderModal: value })),

    removeHistoryOrderById: (order) =>
      set((state) => {
        const updatedDataHistory = state.dataHistory.map((day) => {
          const orderDate = DateTime.fromISO(order.date)
            .setZone(order.timeZone)
            .toISODate();

          if (day.salesDay === orderDate) {
            return {
              ...day,
              count: day.count - 1,
              total:
                day.total -
                (day.orders?.find((o) => o.id === order.id)?.totalPrice || 0),
              orders: day.orders?.filter((o) => o.id !== order.id),
            };
          } else return day;
        });

        return { ...state, dataHistory: updatedDataHistory };
      }),

    removeOrderById: (id) =>
      set((state) => {
        const updatedOrders = state.dataOrders.filter(
          (order) => order.id !== id
        );

        return { ...state, dataOrders: updatedOrders };
      }),

    sortActiveDataOrders: (_id) =>
      set((state) => {
        const updatedList = [...state.dataOrders];

        const sortedOrders = [
          ...updatedList.filter((order) => order.status === 'new'),
          ...updatedList.filter((order) => order.status === 'accepted'),
          ...updatedList.filter(
            (order) => order.status !== 'new' && order.status !== 'accepted'
          ),
        ];

        return { ...state, dataOrders: sortedOrders };
      }),

    assignVDriveProviderOnList: (id, provider) =>
      set((state) => {
        const updatedOrders = state.dataOrders.map((order) => {
          if (order.id === id) {
            return { ...order, vDriveProvider: provider };
          }
          return order;
        });

        return {
          ...state,
          dataOrders: updatedOrders,
        };
      }),

    changeVDriveOnList: (id, vDrive) =>
      set((state) => {
        const updatedOrders = state.dataOrders.map((order) => {
          if (order.id === id) {
            return { ...order, vDrive: vDrive ?? undefined };
          }
          return order;
        });

        return {
          ...state,
          dataOrders: updatedOrders,
        };
      }),
    }),
    { name: 'orders' }
  )
);
